// Stable feature key constants for the frontend.
// These must stay in sync with packages/types/src/index.ts FEATURES.
// Prefer importing from packages/types directly if your bundler supports it.
export const FEATURES = {
  MARKETPLACE_PUBLISH: 'marketplace_publish',
  ASSESSMENTS: 'assessments',
  CANDIDATES_PER_TEST: 'candidates_per_test',
  ADVANCED_PROCTORING: 'advanced_proctoring',
  MEMBERS: 'members',
  AI_CHAT: 'ai_chat',
  AI_TOKENS_MONTHLY: 'ai_tokens_monthly',
  DOCUMENTS: 'documents',
  ADVANCED_ANALYTICS: 'advanced_analytics',
  EXPORT_RESULTS: 'export_results',
  CUSTOM_BRANDING: 'custom_branding',
  API_ACCESS: 'api_access',
  SSO: 'sso',
  AUDIT_LOGS: 'audit_logs',
  PRIORITY_SUPPORT: 'priority_support',
  QUESTION_BANK: 'question_bank',
  COLLECTIONS: 'collections',
  TEAMS: 'teams',
  AI_QUESTIONS_SINGLE: 'ai_questions_single',
  AI_QUESTIONS_BULK: 'ai_questions_bulk',
  AI_QUESTIONS_PER_GENERATION: 'ai_questions_per_generation',
  WORKSHEET_PDF: 'worksheet_pdf',
  CERTIFICATES: 'certificates',
  AI_GRADING: 'ai_grading',
  BULK_GENERATION: 'bulk_generation',
  TEST_SCHEDULING: 'test_scheduling',
  TEST_RETAKES_MAX: 'test_retakes_max',
  ADVANCED_QUESTION_TYPES: 'advanced_question_types',
  QUESTION_BANK_SIZE: 'question_bank_size',
  IELTS_SUITE: 'ielts_suite',
  SAT_SUITE: 'sat_suite',
  AI_IMPORT_REPAIR: 'ai_import_repair',
} as const;

export type FeatureKey = (typeof FEATURES)[keyof typeof FEATURES];

// Absolute ceiling on questions produced by a single AI generation request.
// Mirrors MAX_QUESTIONS_PER_GENERATION on the API (ai-generation.dto.ts). The
// active plan's cap (FEATURES.AI_QUESTIONS_PER_GENERATION) is enforced below
// this by the backend; the frontend reads it via useMaxQuestionsPerGeneration.
export const MAX_QUESTIONS_PER_GENERATION = 100;

// Human-readable labels for upgrade prompts
export const FEATURE_LABELS: Record<FeatureKey, string> = {
  marketplace_publish: 'Marketplace publishing',
  assessments: 'Tests',
  candidates_per_test: 'Candidates per test',
  advanced_proctoring: 'Advanced Proctoring',
  members: 'Organization members',
  ai_chat: 'AI Chat',
  ai_tokens_monthly: 'AI Tokens',
  documents: 'Documents',
  advanced_analytics: 'Advanced Analytics',
  export_results: 'Export Results',
  custom_branding: 'Custom Branding',
  api_access: 'API Access',
  sso: 'SSO',
  audit_logs: 'Audit Logs',
  priority_support: 'Priority Support',
  question_bank: 'Question Bank',
  collections: 'Collections',
  teams: 'Teams',
  ai_questions_single: 'Single AI question generation',
  ai_questions_bulk: 'Bulk AI question generation',
  ai_questions_per_generation: 'Questions per AI generation',
  worksheet_pdf: 'Worksheet PDF export',
  certificates: 'Certificates',
  ai_grading: 'AI Grading',
  bulk_generation: 'Bulk AI generation',
  test_scheduling: 'Test scheduling',
  test_retakes_max: 'Retakes per candidate',
  advanced_question_types: 'Advanced question types',
  question_bank_size: 'Question bank size',
  ielts_suite: 'IELTS-style tests',
  sat_suite: 'Digital SAT practice',
  ai_import_repair: 'AI import repair',
};

export const PLAN_LABELS: Record<string, string> = {
  TRIAL: 'Free Trial',
  STARTER: 'Starter',
  GROWTH: 'Growth',
  PRO: 'Pro',
  // The in-code Free plan an org drops to after the trial (the key is historical).
  EXPIRED: 'Free',
  // Legacy keys retained so old org docs still render a label.
  FREE: 'Free',
  ENTERPRISE: 'Enterprise',
};
